import { useCallback } from 'react';
import type { FileDTO, Folder } from '@29100/shared';
import { api } from '../lib/api';
import { usePagedQuery, useQuery, type PagedResponse } from './useApi';

/**
 * Archivos y carpetas.
 *
 * Todas las rutas llevan `workspaceId`: el backend no deduce el espacio de la
 * sesión, lo exige, y comprueba el acceso en cada llamada. Aquí se respeta esa
 * forma en lugar de esconderla, porque olvidarlo produce un 404 —no un 403— y
 * sería difícil de leer si el parámetro estuviera enterrado.
 */

/** Forma exacta de `GET /api/folders/browse`. */
export interface FolderListing extends PagedResponse {
  folder: Folder | null;
  breadcrumb: { id: string; name: string }[];
  children: Folder[];
  files: FileDTO[];
}

/**
 * El contenido de una carpeta, con los archivos por páginas.
 *
 * `items` son los archivos y se acumulan; `last` trae lo que no se pagina —la
 * carpeta actual, su breadcrumb y sus subcarpetas—, que viaja con cada página.
 * El explorador pedía doscientos archivos y ahí se acababa la carpeta para
 * quien la miraba; los que hubiera detrás no estaban en ninguna pantalla.
 */
export function useFolderContents(
  workspaceId: string | null,
  folderId: string | null,
  refreshKey = 0,
) {
  return usePagedQuery<FileDTO, FolderListing>(
    workspaceId ? '/api/folders/browse' : null,
    (respuesta) => respuesta.files,
    { query: { workspaceId: workspaceId ?? '', folderId: folderId ?? undefined } },
    `${workspaceId}:${folderId}:${refreshKey}`,
  );
}

/** Una carpeta vista desde un selector: dónde está, no qué contiene. */
export interface FolderNode {
  id: string;
  parentId: string | null;
  name: string;
  depth: number;
  /** «Clientes / Acme / 2026». Distingue dos carpetas que se llaman igual. */
  path: string;
}

/**
 * Todas las carpetas del espacio, para elegir una.
 *
 * `useFolderContents` sirve para recorrer y esta para elegir: pedir el primer
 * nivel a `browse` dejaba fuera cualquier subcarpeta, así que no había forma de
 * mandar los documentos a la carpeta donde de verdad iban.
 */
export function useFolderTree(workspaceId: string | null, refreshKey = 0) {
  return useQuery<{ folders: FolderNode[] }>(
    workspaceId ? '/api/folders/tree' : null,
    { query: { workspaceId: workspaceId ?? '' } },
    `arbol:${workspaceId}:${refreshKey}`,
  );
}

export function useFileSearch(workspaceId: string | null, term: string) {
  const listo = Boolean(workspaceId) && term.trim().length >= 2;

  return useQuery<{ files: FileDTO[] }>(
    listo ? '/api/files/search' : null,
    { query: { workspaceId: workspaceId ?? '', q: term.trim() } },
    `${workspaceId}:${term.trim()}`,
  );
}

/**
 * La galería, por páginas y acumulando.
 *
 * Pedía una sola página y pintaba lo que viniera. Como el tamaño por defecto de
 * un listado son cincuenta, quien subía más de cincuenta fotos veía cincuenta y
 * el resto no existía en ninguna pantalla: ni un aviso, ni un botón, y el
 * contador de la cabecera decía «50 fotos» como si fueran todas. No había
 * ningún límite que quitar —el límite es el espacio contratado—, sólo faltaba
 * pedir la página siguiente.
 */
export function usePhotos(workspaceId: string | null, refreshKey = 0) {
  // La API responde `{ photos }`, no `{ files }`.
  return usePagedQuery<FileDTO, { photos: FileDTO[]; nextOffset: number | null }>(
    workspaceId ? '/api/photos' : null,
    (respuesta) => respuesta.photos,
    { query: { workspaceId: workspaceId ?? '' } },
    `${workspaceId}:${refreshKey}`,
  );
}

export function useFavorites(workspaceId: string | null, refreshKey = 0) {
  return usePagedQuery<FileDTO, { files: FileDTO[]; nextOffset: number | null }>(
    workspaceId ? '/api/files/favorites' : null,
    (respuesta) => respuesta.files,
    { query: { workspaceId: workspaceId ?? '' } },
    `${workspaceId}:${refreshKey}`,
  );
}

/**
 * La papelera.
 *
 * `items` son los archivos, que es lo que la pantalla pinta y lo que puede
 * pasar de cincuenta; las carpetas borradas viajan en `last.folders` con su
 * propio orden, sin cursor propio.
 */
export function useTrash(workspaceId: string | null, refreshKey = 0) {
  return usePagedQuery<FileDTO, { files: FileDTO[]; folders: Folder[]; nextOffset: number | null }>(
    workspaceId ? '/api/files/trash' : null,
    (respuesta) => respuesta.files,
    { query: { workspaceId: workspaceId ?? '' } },
    `${workspaceId}:${refreshKey}`,
  );
}

/** Acciones sobre archivos. Devuelven promesas; el toast lo pone la pantalla. */
export function useFileActions(workspaceId: string | null) {
  const requerido = useCallback(() => {
    if (!workspaceId) throw new Error('No hay espacio activo');
    return workspaceId;
  }, [workspaceId]);

  return {
    /** URL prefirmada: el contenido no atraviesa el Worker. */
    download: useCallback(
      async (fileId: string) => {
        const { url } = await api<{ url: string; expiresIn: number }>(
          `/api/files/${fileId}/download`,
          { query: { workspaceId: requerido() } },
        );
        return url;
      },
      [requerido],
    ),

    rename: useCallback(
      (fileId: string, name: string) =>
        api(`/api/files/${fileId}/rename`, {
          method: 'PATCH',
          body: { workspaceId: requerido(), name },
        }),
      [requerido],
    ),

    move: useCallback(
      (fileId: string, folderId: string | null) =>
        api(`/api/files/${fileId}/move`, {
          method: 'PATCH',
          body: { workspaceId: requerido(), folderId },
        }),
      [requerido],
    ),

    favorite: useCallback(
      (fileId: string, on: boolean) =>
        api(`/api/files/${fileId}/favorite`, {
          method: on ? 'POST' : 'DELETE',
          body: on ? { workspaceId: requerido() } : undefined,
          query: on ? undefined : { workspaceId: requerido() },
        }),
      [requerido],
    ),

    /** Reversible: va a la papelera, no se borra. */
    trash: useCallback(
      (fileId: string) =>
        api(`/api/files/${fileId}`, { method: 'DELETE', query: { workspaceId: requerido() } }),
      [requerido],
    ),

    restore: useCallback(
      (fileId: string) =>
        api(`/api/files/${fileId}/restore`, { method: 'POST', body: { workspaceId: requerido() } }),
      [requerido],
    ),

    deleteForever: useCallback(
      (fileId: string) =>
        api(`/api/files/${fileId}/permanent`, {
          method: 'DELETE',
          query: { workspaceId: requerido() },
        }),
      [requerido],
    ),

    createFolder: useCallback(
      (name: string, parentId: string | null) =>
        api<{ folder: Folder }>('/api/folders', {
          method: 'POST',
          body: { workspaceId: requerido(), name, parentId },
        }),
      [requerido],
    ),

    renameFolder: useCallback(
      (folderId: string, name: string) =>
        api(`/api/folders/${folderId}/rename`, {
          method: 'PATCH',
          body: { workspaceId: requerido(), name },
        }),
      [requerido],
    ),

    moveFolder: useCallback(
      (folderId: string, parentId: string | null) =>
        api(`/api/folders/${folderId}/move`, {
          method: 'PATCH',
          body: { workspaceId: requerido(), parentId },
        }),
      [requerido],
    ),

    trashFolder: useCallback(
      (folderId: string) =>
        api(`/api/folders/${folderId}`, { method: 'DELETE', query: { workspaceId: requerido() } }),
      [requerido],
    ),
  };
}
